import { useEffect } from 'react';
import List from '../list';
import {
  CurrentProcess,
  stringToCurrentProcess,
  stringToFileExtractionStatus,
} from '../../models/jobStatus';
import { SnackbarProps } from '../../pages/helper';
import { downloadFile } from '../../lib/services/blob-service';
import {
  Stepper,
  Typography,
  Step,
  StepLabel,
  Grid,
  LinearProgress,
  Button,
} from '@mui/material';
import { steps } from '../dropzone/helper';
import DownloadIcon from '../../assets/download.svg';
import useBatchProcessStore from '../../store/batchProcessStore';

export interface JobStatusProps {
  setSnackbar: (props: SnackbarProps) => void;
}

const JobStatus = (props: JobStatusProps) => {
  const { setSnackbar } = props;
  const {
    batchId,
    isProcessing,
    jobStatus: jobStatusData,
    setIsProcessing,
    isBatchFailure,
    currentProcess,
    isFileUploaded,
    setIsFileUploaded,
    acceptedFiles,
    setAcceptedFiles,
    isDownloadEnabled,
    setIsDownloadEnabled,
    setCurrentProcess,
    rejectedFiles,
  } = useBatchProcessStore();

  const handleRemove = (f: string) => {
    if (!isProcessing)
      setAcceptedFiles(acceptedFiles.filter(file => file.name !== f));
  };

  useEffect(() => {
    if (jobStatusData != undefined && jobStatusData?.length > 0) {
      const batchStatus = jobStatusData.find(
        e => e.RowKey.toLowerCase() === batchId?.toLowerCase(),
      );
      const currentStatusValue = stringToCurrentProcess(
        batchStatus?.JobStatus ?? '',
      );
      const canDownload =
        currentStatusValue === CurrentProcess.MergeActivityComplete ||
        currentStatusValue === CurrentProcess.DataSyncCompleted;

      setIsDownloadEnabled(canDownload);

      if (canDownload) {
        sessionStorage.setItem(batchId?.toString() ?? '', 'true');
        setIsProcessing(false);
      }

      const currentProcessStatus = stringToCurrentProcess(
        jobStatusData[0].JobStatus,
      );

      setCurrentProcess(currentProcessStatus);

      if (
        (stringToCurrentProcess(batchStatus?.JobStatus ?? '') ?? 0) <=
        CurrentProcess.MergeActivityComplete
      ) {
        const files = acceptedFiles.map(file => {
          const fileWithStatus = jobStatusData.find(
            e => e.FileName === file.name,
          );
          if (fileWithStatus) {
            return Object.assign(file, {
              status: stringToFileExtractionStatus(fileWithStatus.FileStatus),
            });
          }
          return file;
        });

        if (files.length > 0) {
          console.log(files.map(e => ({ status: e.status, name: e.name })));
          setAcceptedFiles(files);
        }
      }
    }
  }, [batchId, jobStatusData]);

  const handleCancel = () => {
    setIsProcessing(false);
    setAcceptedFiles([]);
    setSnackbar({
      show: true,
      errorMessage: 'Process has been canceled.',
    });
    setIsFileUploaded(false);
  };

  const handleDownload = async () => {
    const filePath = sessionStorage.getItem('filePath');
    const blobPath = `${filePath}/${batchId}/result-${batchId}.xlsx`;
    await downloadFile(blobPath);
  };

  return (
    <div>
      {acceptedFiles.length > 0 && (
        <div>
          <div className="relative flex items-center py-5">
            <div className="grow border-t border-gray-400"></div>
            <span className="mx-4 shrink text-4xl font-bold">
              Invoice Intelligence
            </span>
            <div className="grow border-t border-gray-400"></div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3" />
            <div className="col-span-6 w-full">
              <Stepper
                activeStep={currentProcess + (isFileUploaded ? 1 : 0)}
                alternativeLabel>
                {steps.map(step => {
                  const stepProps: { completed?: boolean } = {};
                  const labelProps: {
                    optional?: React.ReactNode;
                    error?: boolean;
                  } = {};

                  if (isBatchFailure) {
                    labelProps.optional = (
                      <Typography variant="caption" color="error">
                        {/* Failure */}
                      </Typography>
                    );
                    labelProps.error = true;
                  }

                  return (
                    <Step key={step.value} {...stepProps}>
                      <StepLabel {...labelProps}>{step.value}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </div>
            {isDownloadEnabled && (
              <div className="col-span-3 flex justify-end">
                <div
                  className={`flex w-48 max-w-sm cursor-pointer items-center rounded-lg bg-green-500 p-4 text-white hover:bg-green-700 ${
                    !isDownloadEnabled ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={handleDownload}>
                  <button className="mx-auto flex items-center text-xl">
                    <span className="mr-2">
                      <img
                        height={24}
                        width={24}
                        src={DownloadIcon}
                        className="size-6 fill-white"
                        alt="download"
                      />
                    </span>
                    <span>Download</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mx-5 my-4">
            <Grid container alignItems="center">
              <Grid item xs={10}>
                {isProcessing && (
                  <LinearProgress
                  // variant="determinate"
                  />
                )}
              </Grid>
              <Grid item xs={2}>
                {isProcessing && (
                  <div style={{ marginLeft: '25px' }}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCancel}
                      className="w-full">
                      Cancel
                    </Button>
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        </div>
      )}
      <aside className="mt-4">
        <List
          acceptedFiles={acceptedFiles}
          rejectedFiles={rejectedFiles}
          handleRemoveFile={handleRemove}
          isRemoveEnabled={!isProcessing && batchId === undefined}
          errorId={batchId ?? ''}
        />
      </aside>
    </div>
  );
};

export default JobStatus;
